import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';
import { marked } from 'marked';
import { Message, ExportSummaryResponse } from "../types/chat";
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { APP_MANAGER_LOGO_BASE64 } from '../constants/appManagerLogo';

// Define ApiResponse interface to match the one used in useApi
interface ApiResponse<T> {
  data?: T;
  error?: string;
}

interface ExportPDFOptions {
  messages: Message[];
  fetchWithSession: <T>(url: string, options?: RequestInit, isStream?: boolean) => Promise<ApiResponse<T> | Response>;
  t: (key: string) => string;
  invalidLinksForPDF: string[];
  setSnackbarMessage: (message: string) => void;
  setSnackbarOpen: (open: boolean) => void;
}

/**
 * Helper function to determine the nesting depth of a list item
 */
function getListDepth(li: Element): number {
  let depth = 0;
  let parent = li.parentElement;
  
  while (parent) {
    if (parent.tagName && (parent.tagName.toLowerCase() === 'ul' || parent.tagName.toLowerCase() === 'ol')) {
      depth++;
    }
    parent = parent.parentElement;
  }
  
  return depth;
}

/**
 * Exports the chat conversation to a PDF file
 */
export const exportToPDF = async (options: ExportPDFOptions): Promise<void> => {
  const { messages, fetchWithSession, t, invalidLinksForPDF, setSnackbarMessage, setSnackbarOpen } = options;
  
  try {
    // Helper function to remove emojis from text
    const removeEmojis = (text: string): string => {
      if (!text) return '';
      // This regex pattern removes emojis while preserving regular text, numbers, and punctuation
      return text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '');
    };

    // Create PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      hotfixes: ['px_scaling'],
      compress: true
    });
    
    // Define initial position and margins
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;
    const textWidth = pageWidth - (margin * 2);
    
    // Use a data URL for the logo to avoid CORS issues
    const logoBase64 = APP_MANAGER_LOGO_BASE64; 

    // Create a temporary image to load from the data URL
    const logoImg = new Image();
    logoImg.src = logoBase64;

    // Since we're using a data URL, we can resolve immediately
    // No need to handle network errors or timeouts
    await new Promise<void>((resolve) => {
      logoImg.onload = () => resolve();
    });
    
    // Calculate appropriate size for the logo (maintain aspect ratio)
    const logoAspectRatio = logoImg.width / logoImg.height;
    const logoHeight = 10; // mm
    const logoWidth = logoHeight * logoAspectRatio;
    
    // Add the logo to the PDF
    doc.addImage(logoBase64, 'PNG', margin, margin - 5, logoWidth, logoHeight);
    
    yPos = margin + logoHeight + 5; // Position after the header
    
    // Add current date at the top right
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const currentDate = new Date().toLocaleDateString();
    const dateWidth = doc.getStringUnitWidth(currentDate) * 10 / doc.internal.scaleFactor;
    doc.text(currentDate, pageWidth - margin - dateWidth, margin);
    
    // Fetch summary if we have messages
    if (messages.length > 0) {
      try {
        const response = await fetchWithSession<ExportSummaryResponse>(`${API_ENDPOINTS.EXPORT_SUMMARY}`, {
          method: 'POST',
          body: JSON.stringify({ messages: messages.map(message => ({
            role: message.sender,
            content: message.content,
            timestamp: message.timestamp
          })) }),
        });

        // Handle API response
        if (response instanceof Response) {
          throw new Error('Unexpected response');
        }

        if (response.error || !response.data) {
          throw new Error(response.error || 'Failed to generate summary');
        }

        const summaryData = response.data;
        
        // Add summary section with background
        doc.setFillColor(245, 247, 250); // Light blue-gray background
        doc.roundedRect(margin, yPos, textWidth, 10, 3, 3, 'F');
        
        // Add summary section title
        doc.setFontSize(18);
        doc.setTextColor(33, 33, 33);
        doc.text(t('quickSummary'), margin + 5, yPos + 7);
        yPos += 15;
        
        // Add summary text with wrapping
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        const summaryLines = doc.splitTextToSize(summaryData.summary, textWidth);
        doc.text(summaryLines, margin, yPos);
        yPos += (summaryLines.length * 5) + 5;
        
        // Add insights if available
        if (summaryData.insights && summaryData.insights.length > 0) {
          // Add insights title with background
          doc.setFillColor(245, 247, 250);
          doc.roundedRect(margin, yPos, textWidth, 10, 3, 3, 'F');
          
          doc.setFontSize(16);
          doc.setTextColor(33, 33, 33);
          doc.text(t('keyInsights'), margin + 5, yPos + 7);
          yPos += 15;
          
          // Add each insight as a bullet point
          doc.setFontSize(12);
          doc.setTextColor(60, 60, 60);
          summaryData.insights.forEach((insight: string) => {
            // Check if we need a new page
            if (yPos > pageHeight - margin - 20) {
              doc.addPage();
              yPos = margin;
            }
            
            // Add bullet point
            doc.setFillColor(24, 144, 255);
            doc.circle(margin + 2, yPos - 1.5, 1, 'F');
            
            // Add insight text with wrapping
            const insightLines = doc.splitTextToSize(insight, textWidth - 10);
            doc.text(insightLines, margin + 7, yPos);
            yPos += (insightLines.length * 5) + 3;
          });
          yPos += 5; // Extra space after insights
        }
        
        // Add action items if available
        if (summaryData.action_items && summaryData.action_items.length > 0) {
          // Check if we need a new page
          if (yPos > pageHeight - margin - 30) {
            doc.addPage();
            yPos = margin;
          }
          
          // Add action items title with background
          doc.setFillColor(245, 247, 250);
          doc.roundedRect(margin, yPos, textWidth, 10, 3, 3, 'F');
          
          doc.setFontSize(16);
          doc.setTextColor(33, 33, 33);
          doc.text(t('actionItems'), margin + 5, yPos + 7);
          yPos += 18;
          
          // Add each action item in a box
          doc.setFontSize(12);
          summaryData.action_items.forEach((action: string) => {
            // Check if we need a new page
            if (yPos > pageHeight - margin - 25) {
              doc.addPage();
              yPos = margin;
            }
            
            // Draw action item box
            const actionLines = doc.splitTextToSize(action, textWidth - 12);
            const boxHeight = (actionLines.length * 5) + 8;
            
            // Draw a box with blue left border
            doc.setDrawColor(24, 144, 255); // Blue color
            doc.setFillColor(230, 247, 255); // Light blue background
            doc.roundedRect(margin, yPos - 4, textWidth, boxHeight, 2, 2, 'FD');
            
            // Add blue vertical line on left
            doc.setFillColor(24, 144, 255);
            doc.rect(margin, yPos - 4, 4, boxHeight, 'F');
            
            // Add text
            doc.setTextColor(33, 33, 33);
            doc.text(actionLines, margin + 8, yPos + 2);
            yPos += boxHeight + 5;
          });
          yPos += 5; // Extra space after action items
        }
        
        // Add disclaimer
        doc.setFontSize(10);
        doc.setTextColor(120, 120, 120);
        const disclaimerText = t('disclaimer');
        const disclaimerLines = doc.splitTextToSize(disclaimerText, textWidth);
        doc.text(disclaimerLines, margin, yPos);
        yPos += (disclaimerLines.length * 4) + 15;
      } catch (error) {
        console.error('Failed to fetch summary:', error);
        if (error instanceof Error) {
          if (error.message === 'Not enough messages provided') {
            setSnackbarMessage(t('notEnoughMessages'));
            setSnackbarOpen(true);
          } 
          else if (error.message === 'No messages provided') {
            setSnackbarMessage(t('noMessagesProvided'));
            setSnackbarOpen(true);
          }
          else if (error.message === 'Not Found') {
            setSnackbarMessage(t('chatSessionNotFound'));
            setSnackbarOpen(true);
          }
        }
      }
    }
    
    // Add a new page for the chat report
    doc.addPage();
    yPos = margin;
    
    // Add fancy header on new page too
    doc.setFillColor(245, 247, 250);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    // Add document title with background
    doc.setFillColor(24, 144, 255, 0.1); // Very light blue
    doc.roundedRect(margin, yPos - 5, textWidth, 12, 3, 3, 'F');
    
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text(t('chatReport'), margin + 5, yPos + 4);
    yPos += 20;
    
    // Process each message
    for (const message of messages) {
      // Check if we need a new page
      if (yPos > pageHeight - margin - 40) {
        doc.addPage();
        
        yPos = margin;
      }
      
      // Create a message box with different colors based on sender
      const isUser = message.sender === 'user';
      const boxColor = isUser ? [240, 242, 245] : [255, 255, 255]; // Light gray for user, white for AI
      const borderColor = isUser ? [220, 220, 220] : [240, 240, 240];
      const boxWidth = textWidth;
      
      // Start calculating the height needed for this message
      let messageHeight = 5; // Start with padding
      
      // Add timestamp (calculate height)
      messageHeight += 5;
      
      // Add sender (calculate height)
      messageHeight += 7;
      
      // Add content height if present
      if (message.content) {
        let contentText = message.content;
        let links: {text: string, url: string}[] = [];
        
        // Remove emojis from content before processing
        contentText = removeEmojis(contentText);
        
        if (message.isMarkdown) {
          try {
            // Configure marked options
            marked.use({
              gfm: true,
              breaks: true,
              pedantic: false
            });
            
            // Parse markdown to HTML
            const html = await marked.parse(contentText);
            
            // Create a temporary DOM element
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // Extract links before altering the DOM
            tempDiv.querySelectorAll('a').forEach(link => {
              const url = link.getAttribute('href');
              const text = link.textContent || '';
              if (url && !url.startsWith('#')) {
                links.push({ text, url });
              }
            });
            
            // Format special elements as before...
            // 1. Remove the part that adds URL in parentheses
            tempDiv.querySelectorAll('a').forEach(link => {
              // We no longer add the URL in parentheses
              // Just keep the original link text
            });
            
            // 2. Format code blocks with indicators
            tempDiv.querySelectorAll('pre code').forEach(code => {
              const parent = code.parentElement;
              if (parent) {
                parent.textContent = `\n--- Code ---\n${code.textContent}\n-----------\n`;
              }
            });
            
            // 3. Format inline code with backticks
            tempDiv.querySelectorAll('code:not(pre code)').forEach(code => {
              code.textContent = `\`${code.textContent}\``;
            });
            
            // 3. Add bullet indicators for lists with indentation based on depth
            tempDiv.querySelectorAll('ul li').forEach(li => {
              const depth = getListDepth(li);
              const indentation = '  '.repeat(depth - 1); // 2 spaces per nesting level
              li.textContent = `${indentation}• ${li.textContent}`;
            });
            
            // 4. Add numbers for ordered lists with indentation based on depth
            const ols = tempDiv.querySelectorAll('ol');
            ols.forEach(ol => {
              let counter = 1;
              ol.querySelectorAll('li').forEach(li => {
                const depth = getListDepth(li);
                const indentation = '  '.repeat(depth - 1); // 2 spaces per nesting level
                li.textContent = `${indentation}${counter}. ${li.textContent}`;
                counter++;
              });
            });
            
            // Extract the processed text
            contentText = tempDiv.textContent || '';
            contentText = contentText.replace(/\n{3,}/g, '\n\n').trim();
          } catch (error) {
            console.error('Error parsing markdown:', error);
            // In case of markdown processing error, use the original message with emojis removed
            contentText = removeEmojis(message.content);
            links = [];
          }
        }
        
        const contentLines = doc.splitTextToSize(contentText, textWidth - 10);
        messageHeight += (contentLines.length * 5) + 7;
      }
      
      // Add plotly image height if present
      if (message.generated_html) {
        messageHeight += 65; // Image height + padding
      }
      
      // Check if this message needs a new page
      if (yPos + messageHeight > pageHeight - margin) {
        doc.addPage();
        
        yPos = margin;
      }
      
      if(message.sender === 'user') {
        // Now draw the message box
        doc.setFillColor(boxColor[0], boxColor[1], boxColor[2]);
        doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        doc.roundedRect(margin, yPos, boxWidth, messageHeight, 2, 2, 'FD');
      }
      
      let currentYPos = yPos + 5;
      
      // Add timestamp
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);
      doc.text(new Date(message.timestamp).toLocaleString(), margin + 5, currentYPos);
      currentYPos += 5;
      
      // Add sender
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.setFont('helvetica', 'bold');
      doc.text(message.sender === 'user' ? t('user') : t('assistant'), margin + 5, currentYPos);
      doc.setFont('helvetica', 'normal');
      currentYPos += 7;
      
      // Add content
      if (message.content) {
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        
        // Handle plain text vs markdown
        let contentText = message.content;
        let links: {text: string, url: string}[] = [];
        
        // Remove emojis from content before processing
        contentText = removeEmojis(contentText);
        
        if (message.isMarkdown) {
          try {
            // Configure marked options
            marked.use({
              gfm: true,
              breaks: true,
              pedantic: false
            });
            
            // Parse markdown to HTML
            const html = await marked.parse(contentText);
            
            // Create a temporary DOM element
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // Extract links before altering the DOM
            tempDiv.querySelectorAll('a').forEach(link => {
              const url = link.getAttribute('href');
              const text = link.textContent || '';
              if (url && !url.startsWith('#')) {
                links.push({ text, url });
              }
            });
            
            // 1. Format code blocks with indicators
            tempDiv.querySelectorAll('pre code').forEach(code => {
              const parent = code.parentElement;
              if (parent) {
                parent.textContent = `\n--- Code ---\n${code.textContent}\n-----------\n`;
              }
            });
            
            // 2. Format inline code with backticks
            tempDiv.querySelectorAll('code:not(pre code)').forEach(code => {
              code.textContent = `\`${code.textContent}\``;
            });
            
            // 3. Add bullet indicators for lists with indentation based on depth
            tempDiv.querySelectorAll('ul li').forEach(li => {
              const depth = getListDepth(li);
              const indentation = '  '.repeat(depth - 1); // 2 spaces per nesting level
              li.textContent = `${indentation}• ${li.textContent}`;
            });
            
            // 4. Add numbers for ordered lists with indentation based on depth
            const ols = tempDiv.querySelectorAll('ol');
            ols.forEach(ol => {
              let counter = 1;
              ol.querySelectorAll('li').forEach(li => {
                const depth = getListDepth(li);
                const indentation = '  '.repeat(depth - 1); // 2 spaces per nesting level
                li.textContent = `${indentation}${counter}. ${li.textContent}`;
                counter++;
              });
            });
            
            // Extract the processed text
            contentText = tempDiv.textContent || '';
            contentText = contentText.replace(/\n{3,}/g, '\n\n').trim();
          } catch (error) {
            console.error('Error parsing markdown:', error);
            // In case of markdown processing error, use the original message with emojis removed
            contentText = removeEmojis(message.content);
            links = [];
          }
        }
        
        // Split content into lines for the PDF
        const contentLines = doc.splitTextToSize(contentText, textWidth - 10);
        
        // If we have links to process, we need to handle each line differently
        if (links.length > 0) {
          // Process content line by line
          let currentContentPos = 0;
          for (let i = 0; i < contentLines.length; i++) {
            // Check if we need a new page before rendering this line
            if (currentYPos + 5 > pageHeight - margin) {
              doc.addPage();
              currentYPos = margin;
              
              // When continuing on a new page for AI message, add a continuation indicator
              if (!isUser) {
                doc.setFontSize(10);
                doc.setTextColor(120, 120, 120);
                doc.setFont('helvetica', 'italic');
                doc.text(t('continued'), margin + 5, currentYPos);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(12);
                doc.setTextColor(60, 60, 60);
                currentYPos += 7;
              }
            }
            
            const line = contentLines[i];
            const lineY = currentYPos + (i === 0 ? 0 : 5);
            
            // Calculate line indentation based on leading spaces
            const leadingSpacesMatch = line.match(/^(\s+)/);
            const leadingSpaces = leadingSpacesMatch ? leadingSpacesMatch[1].length : 0;
            const baseXPos = margin + 5;
            
            // Calculate indentation width from space count (approximately)
            const indentWidth = leadingSpaces * (doc.getFontSize() * 0.4 / doc.internal.scaleFactor);
            
            // More efficient link processing: find all links in this line and sort them by position
            const lineStartPos = currentContentPos;
            const lineEndPos = currentContentPos + line.length;
            const linksInThisLine = [];
            
            // Find all links that appear in this line and their positions
            for (const link of links) {
              let searchStartPos = lineStartPos;
              while (searchStartPos < lineEndPos) {
                const linkPos = contentText.indexOf(link.text, searchStartPos);
                if (linkPos === -1 || linkPos >= lineEndPos) break; // Link not in this line
                
                linksInThisLine.push({
                  text: link.text,
                  url: link.url,
                  startPos: linkPos,
                  endPos: linkPos + link.text.length
                });
                
                searchStartPos = linkPos + link.text.length;
              }
            }
            
            // Sort links by their starting position to process them in order
            linksInThisLine.sort((a, b) => a.startPos - b.startPos);
            
            // Now merge any overlapping links (in case we have the same text linked multiple times)
            for (let j = 0; j < linksInThisLine.length - 1; j++) {
              if (j >= linksInThisLine.length - 1) break;
              
              const current = linksInThisLine[j];
              const next = linksInThisLine[j + 1];
              
              // Check for overlap between current and next link
              if (current.endPos > next.startPos) {
                // Keep the first link and remove the overlapping one
                linksInThisLine.splice(j + 1, 1);
                j--; // Recheck this position
              }
            }
            
            // Flag to track if we've found a link in this line
            let foundLinkInLine = linksInThisLine.length > 0;
            
            if (foundLinkInLine) {
              // Process text with links
              let currentLinePos = 0; // Position within this line
              let currentX = baseXPos + indentWidth;
              
              // Process each segment of text
              for (const link of linksInThisLine) {
                // Calculate position within the current line
                const linkLinePos = link.startPos - lineStartPos;
                
                // If there's text before this link, render it
                if (linkLinePos > currentLinePos) {
                  const beforeLinkText = line.substring(currentLinePos, linkLinePos);
                  if (beforeLinkText.trim()) {
                    const beforeTextWidth = doc.getStringUnitWidth(beforeLinkText) * 
                                          doc.getFontSize() / 
                                          doc.internal.scaleFactor;
                    doc.text(beforeLinkText, currentX, lineY);
                    currentX += beforeTextWidth;
                  }
                }
                
                // Render the link
                if (!invalidLinksForPDF.includes(link.url)) {
                  doc.setTextColor(0, 0, 255); // Blue color for valid links only
                  doc.textWithLink(link.text, currentX, lineY, { url: link.url });
                } else {
                  // Add as normal text since the link is invalid
                  doc.text(link.text, currentX, lineY);
                }
                
                // Calculate link width and update position
                const linkWidth = doc.getStringUnitWidth(link.text) * 
                                doc.getFontSize() / 
                                doc.internal.scaleFactor;
                currentX += linkWidth;
                
                // Reset color
                doc.setTextColor(60, 60, 60);
                
                // Update current position
                currentLinePos = link.endPos - lineStartPos;
              }
              
              // If there's text after the last link, render it
              if (currentLinePos < line.length) {
                const afterLinkText = line.substring(currentLinePos);
                if (afterLinkText.trim()) {
                  doc.text(afterLinkText, currentX, lineY);
                }
              }
            } else {
              // No links in this line, just draw it normally
              doc.text(line, baseXPos, lineY);
            }
            
            // Update currentYPos for the next line
            if (i === 0) {
              currentYPos += 5; // First line
            } else {
              currentYPos += 5; // Subsequent lines
            }
            
            // Move to next line's starting position in content
            currentContentPos += line.length + 1; // +1 for newline
          }
        } else {
          // No links - draw regular text with pagination awareness
          let linesProcessed = 0;
          while (linesProcessed < contentLines.length) {
            // How many lines can fit on the current page
            const maxLinesOnPage = Math.floor((pageHeight - currentYPos - margin) / 5);
            const linesToDraw = Math.min(maxLinesOnPage, contentLines.length - linesProcessed);
            
            if (linesToDraw <= 0) {
              // Need a new page
              doc.addPage();
              currentYPos = margin;
              
              // When continuing on a new page for AI message, add a continuation indicator
              if (!isUser) {
                doc.setFontSize(10);
                doc.setTextColor(120, 120, 120);
                doc.setFont('helvetica', 'italic');
                doc.text(t('continued'), margin + 5, currentYPos);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(12);
                doc.setTextColor(60, 60, 60);
                currentYPos += 7;
              }
              continue;
            }
            
            // Extract just the lines that will fit on this page
            const pageLines = contentLines.slice(linesProcessed, linesProcessed + linesToDraw);
            doc.text(pageLines, margin + 5, currentYPos);
            
            // Update counters
            linesProcessed += linesToDraw;
            currentYPos += (linesToDraw * 5);
            
            // If more lines remain, add a new page
            if (linesProcessed < contentLines.length) {
              doc.addPage();
              currentYPos = margin;
              
              // When continuing on a new page for AI message, add a continuation indicator
              if (!isUser) {
                doc.setFontSize(10);
                doc.setTextColor(120, 120, 120);
                doc.setFont('helvetica', 'italic');
                doc.text(t('continued'), margin + 5, currentYPos);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(12);
                doc.setTextColor(60, 60, 60);
                currentYPos += 7;
              }
            }
          }
          
          // Add some padding after all content
          currentYPos += 7;
        }
        
        // Update currentYPos to the final position after rendering all content lines
        // No need to add the full contentLines height since we've been tracking as we go
      }
      
      // Handle Plotly charts
      if (message.generated_html) {
        const plotDiv = document.querySelector(`[data-message-id="${message.id}"]`);
        if (plotDiv) {
          try {
            let plotImage = '';
            
            // Try to use Plotly's native toImage function
            if (plotDiv.querySelector('.js-plotly-plot')) {
              const plotlyEl = plotDiv.querySelector('.js-plotly-plot') as HTMLElement;
              
              if (plotlyEl && window.Plotly) {
                plotImage = await window.Plotly.toImage(plotlyEl, {
                  format: 'png',
                  width: 800,
                  height: 600,
                  scale: 2
                });
              }
            }
            
            // Fallback to toPng if Plotly method failed
            if (!plotImage) {
              plotImage = await toPng(plotDiv as HTMLElement);
            }
            
            // Calculate dimensions to fit within page while preserving aspect ratio
            const imgWidth = textWidth - 10;
            
            // Create a temporary image to get the actual dimensions
            const tempImg = new Image();
            tempImg.src = plotImage;
            await new Promise(resolve => { tempImg.onload = resolve; });
            
            // Calculate height that maintains the aspect ratio
            const aspectRatio = tempImg.height / tempImg.width;
            const imgHeight = imgWidth * aspectRatio;

            // Update the message height to accommodate the actual image height
            // Subtract the default 65 (that was added earlier) and add the actual image height plus padding
            messageHeight = messageHeight - 65 + imgHeight + 10;

            // Check if we need a new page
            if (currentYPos + imgHeight > pageHeight - margin) {
              doc.addPage();
              currentYPos = margin;
            }
            
            // Add the image to the PDF with proper aspect ratio
            doc.addImage(plotImage, 'PNG', margin + 5, currentYPos, imgWidth, imgHeight);
            currentYPos += imgHeight + 5;
          } catch (error) {
            console.error('Failed to convert plot to image:', error);
          }
        }
      }
      
      // Move to position after this message box
      yPos += messageHeight + 10; // Add some spacing between messages
    }
    
    // Add page numbers
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      
      // Add page number
      const pageText = `${i} / ${totalPages}`;
      const pageTextWidth = doc.getStringUnitWidth(pageText) * 10 / doc.internal.scaleFactor;
      doc.text(pageText, pageWidth - margin - pageTextWidth, pageHeight - 10);
      
      // Add footer line
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    }
    
    // Save the PDF
    doc.save('chat-report.pdf');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}; 